import type { Settings, Page, Translation } from 'types';
import type { BlockReviews } from 'types/block-reviews';
import type { InPersonEvents } from 'types/in-person-events';
import type {
  GetStaticPropsContext,
  GetStaticPropsResult,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from 'next';
import type { QueryError } from '../api';
import type { StateLawsListResult } from './state-law';
import type { VpdPage } from 'types/acf';
import type { JobsByTeamResult } from './jobs';
import type { LocationItems } from './locations';
import type { PartnersReviewsResult, ReviewsListResult } from './reviews';
import type { GetDashboardInitialData } from './dashboard';
import type { AlphabetStrain, GetInitialStrainsData } from './strains';
import jwt from 'jsonwebtoken';
import camelcaseKeys from 'camelcase-keys';
import { initializeWpApollo } from 'lib/apollo';
import { request } from 'lib/rest';
import { getStateLawsList } from './state-law';
import { getLanguageCodeFilter, getSettings } from './settings';
import { getPartnersLocations } from './partners';
import { getJobsByTeam } from './jobs';
import { getPartnersReviews } from './reviews';
import { getDashboardInitialData } from './dashboard';
import { getAllStrains, getInitialStrainsData } from './strains';
import { groupItemsByTitleFirstLetter } from 'lib/utils/groupItemsByTitleFirstLetter';

export type AllStatePagesResult = {
  slug: string[];
  locale: string;
}[];

export async function getAllStatePages(): Promise<AllStatePagesResult> {
  try {
    const { error, data } = await request<{ slug: string; locale: string }[]>(
      `/state-pages`,
    );

    if (error) {
      throw { error };
    }

    // make slug an array and filter the empty strings
    // e.g. '/medical-card/ohio' -> ['medical-card', 'ohio']
    return data.items.map(({ slug, locale }) => {
      return {
        slug: slug.split('/').filter(Boolean),
        locale,
      };
    });
  } catch (error) {
    console.log(`error getting all state pages:`, JSON.stringify(error));

    throw error;
  }
}

export async function getPageData({
  slug,
  locale,
  preview,
}: {
  slug: string;
  locale: string;
  preview?: boolean;
}): Promise<Page> {
  const fields =
    'id,title,date,modified,slug,link,content,seo,acf,translations,language,type';
  const { error, data } = await request<Page[]>(
    `/pages?path=${slug}&_fields=${fields}${preview ? '&status=draft' : ''}`,
    {
      headers: {
        'Accept-Language': locale,
      },
    },
  );

  if (error) {
    throw error;
  }

  return camelcaseKeys(data.items[0], { deep: true });
}

export type FrontPage = {
  page: Page;
  settings: Settings;
  isPreview: boolean;
  variant?: 'test' | 'control' | null;
  featureFlag?: string | null;
  posthogBootstrap?: {
    distinctID: string;
    featureFlags: Record<string, string | boolean>;
  } | null;
  initialStateCode?: string | null;
};

export async function getFrontPage({
  locale,
  preview,
  variant,
  featureFlag,
  posthogBootstrap,
  initialStateCode,
}: GetServerSidePropsContext & {
  variant?: 'test' | 'control' | null;
  featureFlag?: string | null;
  posthogBootstrap?: {
    distinctID: string;
    featureFlags: Record<string, string | boolean>;
  };
  initialStateCode?: string | null;
}): Promise<GetServerSidePropsResult<FrontPage>> {
  try {
    const HOMEPAGE_STATIC = {
      id: 5,
      date: '2022-01-03T02:46:16',
      modified: '2025-05-05T08:51:27',
      slug: 'homepage',
      type: 'page',
      link: '/',
      title: 'Homepage',
      content: '',
      acf: {
        pageType: 'home',
        hasReferral: true,
        hero: {
          title:
            'Get approved for your <span class="text-purple-500">medical card</span> online in minutes',
          description: 'Quick. Easy. 100% Online. Money-back guarantee.',
          subTitle: 'Live Well With Cannabis',
          formId: '',
          formType: 'partner',
          formTitle: '',
          formLabel: '',
          formSubmitText: 'Get Started',
          image: {
            id: '73144',
            src: 'https://admin.leafwell.com/app/uploads/2023/11/card_optim.png',
            width: 640,
            height: 794,
            srcSet:
              'https://b2628119.smushcdn.com/2628119/app/uploads/2023/11/card_optim.png?size=256x318&lossy=2&strip=1&webp=1 256w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/11/card_optim-322x400.png?lossy=2&strip=1&webp=1 322w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/11/card_optim.png?size=512x635&lossy=2&strip=1&webp=1 512w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/11/card_optim-564x700.png?lossy=2&strip=1&webp=1 564w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/11/card_optim.png?lossy=2&strip=1&webp=1 640w',
            alt: '',
          },
          imageAlignment: 'right',
          steps: [
            {
              title: 'Quick and Easy',
              description:
                '<p>Seamless technology that connects you to a network of doctors and gets you <strong>certified online in minutes.</strong></p>\n',
            },
            {
              title: 'Money-Back Guarantee',
              description:
                '<p>Affordable and transparent pricing, every step of the way. Get <strong>approved or your money back</strong>.</p>\n',
            },
          ],
        },
        blocks: [
          {
            acfFcLayout: 'intro',
            title: 'Live Better With Cannabis',
            description:
              'Our <strong>friendly team</strong>  is on hand to <strong>help you throughout the process</strong> of getting your <strong>medical marijuana card online</strong>.',
            descriptionSize: 'normal',
            topIcon: null,
            button: '',
            alignment: 'center',
            sectionId: '',
            note: '',
          },
          {
            acfFcLayout: 'youtube_video',
            sectionId: '',
            title: '',
            content: '',
            variant: 'both',
            videoSize: 'large',
            youtubeVideos: [
              {
                youtubeVideoId: 'PlJBfcTwZ10',
                image: {
                  id: '51924',
                  src: 'https://admin.leafwell.com/app/uploads/2023/01/emily.jpg',
                  width: 1320,
                  height: 740,
                  srcSet:
                    'https://b2628119.smushcdn.com/2628119/app/uploads/2023/01/emily.jpg?size=256x143&lossy=2&strip=1&webp=1 256w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/01/emily-400x224.jpg?lossy=2&strip=1&webp=1 400w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/01/emily.jpg?size=512x287&lossy=2&strip=1&webp=1 512w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/01/emily-700x392.jpg?lossy=2&strip=1&webp=1 700w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/01/emily-768x431.jpg?lossy=2&strip=1&webp=1 768w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/01/emily-1024x574.jpg?lossy=2&strip=1&webp=1 1024w, https://b2628119.smushcdn.com/2628119/app/uploads/2023/01/emily.jpg?lossy=2&strip=1&webp=1 1320w',
                  alt: 'Emily Fisher, CEO at Leafwell',
                },
                playButtonStyle: 'primary',
              },
            ],
          },
          {
            acfFcLayout: 'seen_on',
            title: 'As Seen In',
            items: [
              {
                logo: {
                  id: '26407',
                  src: 'https://admin.leafwell.com/app/uploads/2022/01/benzinga@2x.png',
                  width: 24,
                  height: 92,
                  srcSet: false,
                  alt: 'Benzinga',
                },
                url: '',
              },
              {
                logo: {
                  id: '26416',
                  src: 'https://admin.leafwell.com/app/uploads/2022/01/insider@2x.png',
                  width: 237,
                  height: 92,
                  srcSet: false,
                  alt: 'Insider',
                },
                url: '',
              },
              {
                logo: {
                  id: '26417',
                  src: 'https://admin.leafwell.com/app/uploads/2022/01/leafly@2x.png',
                  width: 149,
                  height: 92,
                  srcSet: false,
                  alt: 'Leafly',
                },
                url: '',
              },
              {
                logo: {
                  id: '26412',
                  src: 'https://admin.leafwell.com/app/uploads/2022/01/forbes@2x.png',
                  width: 168,
                  height: 92,
                  srcSet: false,
                  alt: 'Forbes',
                },
                url: '',
              },
              {
                logo: {
                  id: '26411',
                  src: 'https://admin.leafwell.com/app/uploads/2022/01/cannigma@2x.png',
                  width: 197,
                  height: 92,
                  srcSet: false,
                  alt: 'The Cannigma',
                },
                url: '',
              },
              {
                logo: {
                  id: '26415',
                  src: 'https://admin.leafwell.com/app/uploads/2022/01/high-times@2x.png',
                  width: 175,
                  height: 92,
                  srcSet: false,
                  alt: 'High Times',
                },
                url: '',
              },
            ],
          },
          {
            acfFcLayout: 'reviews',
            sectionId: '',
            title:
              '<span class="text-green">Trusted</span> By <em data-highlight="circle">1 Million</em>Monthly Users',
            intro: '',
            trustpilot: true,
            showTrustSignals: false,
            variant: 'full',
            quoted: false,
          },
          {
            acfFcLayout: 'stats_highlight',
            title:
              'Leafwell is at the forefront of cutting-edge scientific research.',
            description:
              'Our extensive library of evidence-based articles and studies delivers the information you need on cannabis.<br />\r\n<br />\r\nJourney with Leafwell and ask us anything you want to know about medical marijuana.<br />\r\n',
            button: {
              title: 'Learn about cannabis',
              url: 'https://leafwell.com/blog',
              target: '_blank',
            },
            image: {
              id: '23470',
              src: 'https://admin.leafwell.com/app/uploads/2021/12/home-highlight-stats.png',
              width: '490',
              height: '662',
              srcSet:
                'https://b2628119.smushcdn.com/2628119/app/uploads/2021/12/home-highlight-stats.png?size=256x346&lossy=2&strip=1&webp=1 256w, https://b2628119.smushcdn.com/2628119/app/uploads/2021/12/home-highlight-stats-444x600.png?lossy=2&strip=1&webp=1 444w, https://b2628119.smushcdn.com/2628119/app/uploads/2021/12/home-highlight-stats.png?lossy=2&strip=1&webp=1 490w',
              alt: '',
            },
            items: [
              {
                value: '+3000',
                label: 'More Than 3,000 Studies',
                image: {
                  id: '21918',
                  src: 'https://admin.leafwell.com/app/uploads/2021/09/icon-graph.svg',
                  width: '0',
                  height: '0',
                  srcSet: false,
                  alt: '',
                },
              },
              {
                value: '+200k',
                label: 'Patients Enrolled',
                image: {
                  id: '21919',
                  src: 'https://admin.leafwell.com/app/uploads/2021/09/icon-people.svg',
                  width: '0',
                  height: '0',
                  srcSet: false,
                  alt: '',
                },
              },
              {
                value: '+100',
                label: 'Medical Conditions',
                image: {
                  id: '21920',
                  src: 'https://admin.leafwell.com/app/uploads/2021/09/icon-search.svg',
                  width: '0',
                  height: '0',
                  srcSet: false,
                  alt: '',
                },
              },
            ],
          },
          {
            acfFcLayout: 'sign_up_form',
            sectionId: '',
            layout: 'right',
            title: 'Sign Up',
            formDescription: '',
            address: '',
            subTitle:
              'Fill out this quick form and we’ll get the process started for you.',
            formType: 'card',
            formUrl: '',
          },
        ],
      },
      seo: {
        title: 'Empowering Healthier Lives with Medical Cannabis',
        description:
          'Speak to a medical marijuana doctor online in minutes with Leafwell and qualify for a medical card. Get your cannabis certificate or rec now via telehealth!',
        canonical: 'https://leafwell.com/',
        images: [
          {
            sourceUrl:
              'https://admin.leafwell.com/app/uploads/2022/01/share-1024x538.jpg',
            altText: '',
            mediaDetails: {
              width: 1024,
              height: 538,
            },
          },
        ],
        metaRobotsNoarchive: false,
        metaRobotsNofollow: false,
        metaRobotsNoindex: false,
        opengraph: {
          title: 'Empowering Healthier Lives with Medical Cannabis',
          description:
            'Speak to a medical marijuana doctor online in minutes with Leafwell and qualify for a medical card. Get your cannabis certificate or rec now via telehealth!',
          type: 'website',
        },
        redirect: null,
      },
      language: 'en',
      translations: [
        {
          uri: '/',
          slug: 'en',
          locale: 'en',
        },
        {
          uri: '/es/',
          slug: 'es',
          locale: 'es_ES',
        },
      ],
    };

    // static data doesn't match Page exactly (e.g. numeric id)
    const homepage = HOMEPAGE_STATIC as unknown as Page;
    const settings = await getSettings(locale);

    if (!settings) {
      return {
        notFound: true,
      };
    }

    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (homepage?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] =
          homepage?.translations;

        if (
          locale === 'en' ||
          homepage?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? '/'
                : homepage?.translations?.find(({ slug }) => slug === 'en')
                    ?.uri,
            slug: 'x-default',
          });
        }
        return annotations;
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    return {
      props: {
        settings: {
          ...settings,
          pageTranslations: homepage.translations,
          languageAlternates,
        },
        page: homepage,
        variant: variant ?? null,
        featureFlag: featureFlag ?? null,
        initialStateCode: initialStateCode ?? null,
        posthogBootstrap: posthogBootstrap ?? null,
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`error getting home:`, error);

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}

export type PageProps = {
  page: Page;
  settings: Settings;
  dashboard?: GetDashboardInitialData;
  lawStatuses?: StateLawsListResult;
  locations?: LocationItems;
  reviews?: ReviewsListResult;
  partnersReviews?: PartnersReviewsResult['partnersReviews'];
  strains?: GetInitialStrainsData;
  strainsList?: {
    [index: string]: AlphabetStrain[];
  };
  teams?: JobsByTeamResult['teams'];
  vpdEvents?: VpdPage;
  inPersonEvents?: InPersonEvents;
  variant?: 'test' | 'control' | null;
  featureFlag?: string;
  posthogBootstrap?: {
    distinctID: string;
    featureFlags: Record<string, string | boolean>;
  };
  siteTitle?: string;
  isPreview: boolean;
};

export async function getPage({
  locale,
  params,
  preview,
  variant = 'control',
}: GetStaticPropsContext & { variant?: 'control' | 'test' }): Promise<
  GetStaticPropsResult<PageProps>
> {
  const slug = Array.isArray(params.slug) ? params.slug.join('/') : params.slug;

  try {
    const languageCode = getLanguageCodeFilter(locale);

    const [page, settings] = await Promise.all([
      getPageData({ locale, slug, preview }),
      getSettings(locale),
    ]);

    // Since our partners use urls like `/partner-name` we need a way to check if the current url
    // is indeed a partner or an ad, fetch its data (`/partner/partner-name` || `/ad/partner-name`) and redirect to the correct
    // medical card page with the state defined or with the user's state
    if (!page && settings && params.slug?.length === 1) {
      const { error: partnerError, data: partnerData } = await request(
        `/partner?slug=${params.slug[0]}`,
      );

      if (partnerError) {
        throw { partnerError };
      }

      if (partnerData && partnerData?.totalItems > 0) {
        let state = null;
        const { statePages } = settings;
        const { states } = partnerData.items[0].acf;
        // get the state slug using state code
        if (states?.length) {
          state = statePages.find(item => item.code === states[0])?.slug;
        }

        const { ad, is_placeholder: isPlaceholder } = partnerData.items[0].acf;

        if (ad?.length === 0) {
          // redirect to `medical-card` with or without state and the `utm_source` as the partner name
          return {
            redirect: {
              permanent: true,
              destination: `/medical-card${
                state ? '/' + state : ''
              }?utm_source=${params.slug}`,
            },
          };
        }

        if (ad && !isPlaceholder) {
          // redirect to `medical-card` with or without state and the `utm_source` & `utm_campaign` as the partner name
          return {
            redirect: {
              permanent: true,
              destination: `/medical-card${
                state ? '/' + state : ''
              }?utm_source=${params.slug}&utm_campaign=${ad.slug}`,
            },
          };
        }

        if (ad && isPlaceholder) {
          // redirect to `medical-card` with or without state and the `utm_campaign` as the partner name
          return {
            redirect: {
              permanent: true,
              destination: `/medical-card${
                state ? '/' + state : ''
              }?utm_campaign=${ad.slug}`,
            },
          };
        }
      }
    }

    if (!page || !settings) {
      return {
        notFound: true,
      };
    }

    const pageLanguage = page.language;

    // redirect locale page to correct URL
    // ex: /slug-in-spanish -> /es/slug-in-spanish
    if (!preview && pageLanguage && pageLanguage !== locale) {
      return page.slug
        ? {
            redirect: {
              permanent: true,
              destination: page.link,
            },
          }
        : {
            notFound: true,
          };
    }

    const pageType = page.acf.pageType;

    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (page?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] = page?.translations;

        if (
          locale === 'en' ||
          page?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? `/${slug}`
                : page?.translations?.find(({ slug }) => slug === 'en')?.uri,
            slug: 'x-default',
          });
        }
        return annotations;
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    let pageData: PageProps = {
      page,
      settings: {
        ...settings,
        pageTranslations: page.translations,
        languageAlternates,
      },
      isPreview: !!preview,
    };

    const reviewsBlock =
      page.acf.blocks &&
      (page.acf.blocks.find(
        ({ acfFcLayout }) => acfFcLayout == 'reviews',
      ) as BlockReviews);
    const isTrustPilotWidget = reviewsBlock ? reviewsBlock.trustpilot : false;

    if (reviewsBlock && !isTrustPilotWidget) {
      const client = initializeWpApollo();
      const { error: partnersReviewsError, partnersReviews } =
        await getPartnersReviews({ client });

      if (partnersReviewsError) {
        throw partnersReviewsError;
      }
      pageData = { ...pageData, partnersReviews: partnersReviews };
    }

    if (pageType === 'careers') {
      const { teams } = await getJobsByTeam();

      pageData = { ...pageData, teams };
    }

    if (pageType === 'partners_map') {
      const client = initializeWpApollo();
      const { error: partnerLocationsError, locations } =
        await getPartnersLocations({
          client,
          languageCode,
        });

      if (partnerLocationsError) {
        throw partnerLocationsError;
      }

      pageData = {
        ...pageData,
        locations,
      };
    }

    if (pageType === 'dashboard') {
      const client = initializeWpApollo();
      const dashboard = await getDashboardInitialData({
        client,
        languageCode,
        locale,
      });

      if ((dashboard as QueryError)?.error) {
        throw (dashboard as QueryError)?.error;
      }

      pageData = {
        ...pageData,
        dashboard: dashboard as GetDashboardInitialData,
      };
    }

    if (pageType === 'strains_archive') {
      const client = initializeWpApollo();
      const strains = await getInitialStrainsData({
        client,
        languageCode,
      });

      if ((strains as QueryError)?.error) {
        throw (strains as QueryError)?.error;
      }

      pageData = { ...pageData, strains: strains as GetInitialStrainsData };
    }

    if (slug === 'cannabis-strains' || slug === 'cepas-de-cannabis') {
      const strains = await getAllStrains({ locale });
      const strainsList = groupItemsByTitleFirstLetter(strains);

      pageData = { ...pageData, strainsList };
    }

    if (pageType === 'laws') {
      const lawStatuses = await getStateLawsList(locale);
      pageData = { ...pageData, lawStatuses };
    }

    if (pageType === 'affiliate_landing') {
      pageData = {
        ...pageData,
        vpdEvents: pageData?.page?.acf?.vpdPage,
      };
    }

    if (pageType === 'card' || pageType === 'clinic') {
      let inPersonEvents = null;
      const stateCode = pageData?.page?.acf?.states?.[0]?.toLowerCase();
      const { error: inPersonEventsError, data: inPersonEventsData } =
        await request(`/in-person-events?path=${stateCode}&_fields=acf`);

      if (inPersonEventsError) {
        throw inPersonEventsError;
      }

      if (inPersonEventsData && inPersonEventsData.totalItems > 0) {
        inPersonEvents = camelcaseKeys(inPersonEventsData?.items[0]?.acf, {
          deep: true,
        });
      }

      pageData = {
        ...pageData,
        inPersonEvents,
      };
    }

    return {
      props: {
        ...pageData,
        variant,
      },
    };
  } catch (error) {
    console.log(`error getting page ${slug}:`, JSON.stringify(error));

    throw error;
  }
}

export async function getPageServerSide({
  slug,
  locale,
  preview,
  variant,
  featureFlag,
  staticPage,
  posthogBootstrap,
}: GetServerSidePropsContext & {
  variant?: 'control' | 'test' | null;
  slug: string;
  featureFlag?: string;
  staticPage?: any;
  posthogBootstrap?: {
    distinctID: string;
    featureFlags: Record<string, string | boolean>;
  };
}): Promise<GetServerSidePropsResult<PageProps>> {
  try {
    const [page, settings] = await Promise.all([
      getPageData({ locale, slug, preview }),
      getSettings(locale),
    ]);

    if (!page || !settings) {
      return {
        notFound: true,
      };
    }

    const pageLanguage = page.language;

    // redirect locale page to correct URL
    // ex: /slug-in-spanish -> /es/slug-in-spanish
    if (!preview && pageLanguage && pageLanguage !== locale) {
      return page.slug
        ? {
            redirect: {
              permanent: true,
              destination: page.link,
            },
          }
        : {
            notFound: true,
          };
    }

    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (page?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] = page?.translations;

        if (
          locale === 'en' ||
          page?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? `/${slug}`
                : page?.translations?.find(({ slug }) => slug === 'en')?.uri,
            slug: 'x-default',
          });
        }
        return annotations;
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    let pageData: PageProps = {
      page,
      settings: {
        ...settings,
        pageTranslations: page.translations,
        languageAlternates,
      },
      isPreview: !!preview,
    };

    const pageType = page.acf.pageType;

    if (pageType === 'card' || pageType === 'clinic') {
      let inPersonEvents = null;
      const stateCode = page?.acf?.states?.[0]?.toLowerCase() ?? 'fl';
      const { error: inPersonEventsError, data: inPersonEventsData } =
        await request(`/in-person-events?path=${stateCode}&_fields=acf`);

      if (inPersonEventsError) {
        throw inPersonEventsError;
      }

      if (inPersonEventsData && inPersonEventsData.totalItems > 0) {
        inPersonEvents = camelcaseKeys(inPersonEventsData?.items[0]?.acf, {
          deep: true,
        });
      }

      pageData = {
        ...pageData,
        inPersonEvents,
      };
    }

    return {
      props: {
        ...pageData,
        variant: variant ?? null,
        featureFlag: featureFlag ?? null,
        posthogBootstrap: posthogBootstrap ?? null,
      },
    };
  } catch (error) {
    console.log(`error getting page ${slug}:`, JSON.stringify(error));

    throw error;
  }
}

export function getResearchIframeUrl(): string | null {
  let token = '';

  try {
    token = jwt.sign(
      {
        resource: { dashboard: 51 },
        params: {},
      },
      process.env.METABASE_SECRET_KEY,
    );

    return `${process.env.METABASE_SITE_URL}/embed/dashboard/${token}#bordered=false&titled=false`;
  } catch (error) {
    console.log(error);

    return null;
  }
}
